<script lang="ts" setup>
import { lazyObserve, lazyUnobserve } from '@/observer';
import { ref, onMounted, onBeforeUnmount, computed } from 'vue';

/** 图片组件属性定义 */
const props = withDefaults(
    defineProps<{
        // 图片地址
        src: string;
        // 是否启用懒加载, 默认为 false
        lazy?: boolean;
    }>(),
    {
        // 默认不启用懒加载
        lazy: false,
    },
);

/** 组件事件定义 */
const emit = defineEmits<{ onError: [] }>();

// 图片 DOM 元素引用
const imgRef = ref<HTMLImageElement | null>(null);

// 图片是否加载失败
const isError = ref(false);

// 是否已经进入懒加载可视区域 (用于控制图片 src 的设置)
const hasEnteredLazyArea = ref(false);

// 计算属性: 判断是否应该显示图片 src
const shouldShowSrc = computed(() => {
    return !props.lazy || hasEnteredLazyArea.value;
});

/**
 * 图片加载错误处理函数
 * 当图片加载失败时触发, 设置错误状态并向父组件发送错误事件
 */
const handleImgError = () => {
    isError.value = true;
    emit('onError');
};

/**
 * 懒加载回调函数配置
 * 当元素进入可视区域时会触发 onExposure 回调
 */
const lazyCallbacks = {
    onExposure: () => {
        // 防止重复触发: 如果已经进入懒加载区域, 直接返回
        if (hasEnteredLazyArea.value) return;

        // 标记已进入懒加载区域, 此时可以开始加载图片
        hasEnteredLazyArea.value = true;

        // 停止观察当前元素, 避免重复触发回调
        if (imgRef.value) {
            lazyUnobserve(imgRef.value);
        }
    },
};

// 组件挂载后的初始化逻辑
onMounted(() => {
    // 只有启用懒加载时才需要启动观察器
    if (props.lazy && imgRef.value) {
        lazyObserve(imgRef.value, lazyCallbacks);
    }
});

// 组件卸载时的清理逻辑
onBeforeUnmount(() => {
    // 确保清理观察器, 避免内存泄漏
    if (props.lazy && imgRef.value) {
        lazyUnobserve(imgRef.value);
    }
});
</script>

<template>
    <!-- 正常状态: 显示图片元素 (背景色自然作为加载占位符) -->
    <img
        v-if="!isError"
        ref="imgRef"
        alt=""
        v-bind="shouldShowSrc ? { src: props.src } : {}"
        class="image"
        @error="handleImgError"
    />

    <!-- 错误状态: 显示错误占位符 -->
    <div v-else class="image_error"></div>
</template>

<style scoped lang="scss">
@use './Image.scss';
</style>
